// Capacity payments (CRM / Greek flexibility remuneration mechanism):
// fixed EUR/MW/year availability revenue on installed BESS power.
// No market price series — the rate is a user assumption.

export const DEFAULT_CAPACITY_PAYMENTS_CONFIG = {
  eurPerMwYear: 0,
  revenueTreatment: "DISCLOSURE_ONLY",
  termYears: "", // empty = full project horizon
};

export const CAPACITY_PAYMENT_TREATMENT_OPTIONS = [
  {
    value: "DISCLOSURE_ONLY",
    label: "Disclosure only / upside case",
  },
  {
    value: "CONTRACTED_BASE_CASE",
    label: "Contracted — included in base case",
  },
];

export const CAPACITY_PAYMENTS_HELPER_TEXT =
  "Fixed EUR/MW/year availability revenue on installed BESS discharge power (e.g. capacity remuneration mechanism or flexibility remuneration award). Flat nominal for the configured term; assumes 100% availability, no penalties or clawbacks. 'Contracted' adds it to base-case NPV/IRR/DSCR and is available for merchant arbitrage settlement only — lenders will require the underlying award as evidence.";

const VALID_TREATMENTS = new Set(
  CAPACITY_PAYMENT_TREATMENT_OPTIONS.map((option) => option.value)
);

function parseFiniteNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function normalizeCapacityPaymentsConfig(source = {}) {
  const treatment = source?.revenueTreatment ?? source?.revenue_treatment;
  const termRaw = source?.termYears ?? source?.term_years;
  return {
    eurPerMwYear: parseFiniteNumber(
      source?.eurPerMwYear ?? source?.eur_per_mw_year,
      DEFAULT_CAPACITY_PAYMENTS_CONFIG.eurPerMwYear
    ),
    revenueTreatment: VALID_TREATMENTS.has(treatment)
      ? treatment
      : DEFAULT_CAPACITY_PAYMENTS_CONFIG.revenueTreatment,
    termYears:
      termRaw === "" || termRaw == null
        ? ""
        : Math.max(0, Math.floor(parseFiniteNumber(termRaw, 0))),
  };
}

export function buildCapacityPaymentsPayload(source = {}) {
  const normalized = normalizeCapacityPaymentsConfig(source);
  return {
    enabled: true,
    eurPerMwYear: normalized.eurPerMwYear,
    revenueTreatment: normalized.revenueTreatment,
    termYears:
      normalized.termYears === "" || normalized.termYears === 0
        ? null
        : normalized.termYears,
  };
}

// Below this size, participation in capacity/flexibility remuneration
// mechanisms is not typical in the Greek market (they target larger,
// dispatchable units); eligibility must be verified case by case.
export const CAPACITY_ELIGIBILITY_MIN_MW = 1;

export function validateCapacityPaymentsConfig({
  enabled,
  capacityConfig = {},
  settlementIsArbitrage = true,
  installedBessPowerMw = null,
} = {}) {
  if (!enabled) return { errors: [], warnings: [] };

  const errors = [];
  const warnings = [];
  const normalized = normalizeCapacityPaymentsConfig(capacityConfig);

  if (normalized.eurPerMwYear < 0) {
    errors.push("Capacity payment rate must be greater than or equal to 0 EUR/MW/year.");
  }
  if (
    normalized.revenueTreatment === "CONTRACTED_BASE_CASE" &&
    !settlementIsArbitrage
  ) {
    errors.push(
      "Contracted capacity payments are supported only for merchant arbitrage settlement. Use 'Disclosure only / upside case' for tolling structures."
    );
  }
  if (normalized.eurPerMwYear === 0) {
    warnings.push(
      "Capacity payments are selected but the rate is zero, so capacity payment revenue will be zero."
    );
  }
  const installedMw = Number(installedBessPowerMw);
  if (
    Number.isFinite(installedMw) &&
    installedMw > 0 &&
    installedMw < CAPACITY_ELIGIBILITY_MIN_MW &&
    normalized.eurPerMwYear > 0
  ) {
    warnings.push(
      `Eligibility warning: installed BESS power is ${installedMw} MW. Units below ~${CAPACITY_ELIGIBILITY_MIN_MW} MW are not typically eligible for capacity remuneration / flexibility mechanisms in the Greek market — these target larger, dispatchable units. Verify eligibility with the applicable framework before relying on this revenue; consider removing it for small behind-the-meter systems.`
    );
  }
  return { errors, warnings };
}
